import { useState } from 'react';
import { Path, useForm, type SubmitErrorHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type OnSubmitHandler } from '../../../../types/forms.typed';
import {
  type CloseBrothersForm,
  type QueryParameters,
  FEMALE_TITLES,
  InitialFormValues,
  closebrothersFormSchema,
} from '../../queries/schema/carFinance.schema';
import { ResidentialDetails } from './panels/ResidentialDetails/ResidentialDetails';
import { EmploymentDetails } from './panels/EmploymentDetails/EmploymentDetails';
import { PersonalDetails } from '../../components/Panels/PersonalDetails/PersonalDetails';
import { BankingInformation } from './panels/BankingInformation/BankingInformation';
import { DataProtectionAndConsents } from './panels/DataProtectionAndConsents/DataProtectionAndConsents';

import { useSubmitFinanceForm } from '../../queries/hooks/useSubmitFinanceForm';
import { useValidateStep } from '../../queries/hooks/useValidateStep';

import { formatDateOfBirth } from '../formatting';
import { Step1Content } from '../shared/Step1Content';
import {
  clearableRequiredFieldsDefaultValuesBankingDetails,
  clearableRequiredFieldsDefaultValuesCurrentAddress,
  clearableRequiredFieldsDefaultValuesEmploymentDetails,
  clearableRequiredFieldsDefaultValuesPersonalDetails,
  clearableRequiredFieldsDefaultValuesPreviousAddress,
} from '../shared/constants/clearableRequiredDefaultValues';
import { THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_CLOSE_BROTHERS } from './panels/ResidentialDetails/constants';
import {
  CLOSE_BROTHERS_MAX_AGE,
  CLOSE_BROTHERS_MAX_FINANCE_AMOUNT,
  CLOSE_BROTHERS_MIN_AGE,
} from './constants';

import { typeSafeObjectKeys } from '@dist-marketplace-frontend/utils';
import {
  TOTAL_STEPS,
  STEP_1_FIELDS,
  STEP_2_FIELDS,
  STEP_3_FIELDS,
  STEP_4_FIELDS,
  STEP_5_FIELDS,
  type StepConfig,
} from './CloseBrothersStepLogic';
import { useProviderFormNavigation } from '../shared/useProviderFormNavigation';
import { ProviderFormShell } from '../shared/ProviderFormShell';
import { scrollToFirstError } from '../shared/scrollToFirstError';
import { setRequiredFieldErrors } from '../shared/setRequiredFieldErrors';

type CloseBrothersFinanceFormProps = {
  query: QueryParameters;
  onSubmitCallback: () => void;
  onSubmitErrorCallback: (errorFields: Array<string>) => void;
  onStepChange?: (step: number) => void;
  initialFormData: InitialFormValues;
  adId: string;
};

const CloseBrothersFinanceForm = (props: CloseBrothersFinanceFormProps) => {
  const {
    query,
    onSubmitCallback,
    onSubmitErrorCallback,
    onStepChange,
    initialFormData,
    adId,
  } = props;

  const { currentStep, goToNextStep, goToPrevStep } = useProviderFormNavigation(
    TOTAL_STEPS,
    onStepChange,
  );
  const [introConsent, setIntroConsent] = useState(false);

  const methods = useForm<CloseBrothersForm>({
    resolver: zodResolver(closebrothersFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: {
      personalDetails: {
        ...clearableRequiredFieldsDefaultValuesPersonalDetails,
        countryOrigin: '',
        maidenName: '',
        numberOfDependents: '',
      },
      employmentDetails: {
        ...clearableRequiredFieldsDefaultValuesEmploymentDetails,
        industry: '',
        type: '',
        employerPhone: '',
        employerAddress: {
          addressLine1: '',
          addressLine2: '',
          town: '',
          county: '',
        },
      },
      bankingDetails: {
        ...clearableRequiredFieldsDefaultValuesBankingDetails,
        yearsWithBank: '',
      },
      previousAddress: {
        ...clearableRequiredFieldsDefaultValuesPreviousAddress,
        town: '',
        timeAtAddress: {
          years: '',
        },
      },
      currentAddress: {
        ...clearableRequiredFieldsDefaultValuesCurrentAddress,
        town: '',
      },
      loanDetails: {
        ...initialFormData.loanDetails,
        tradeInValue: 0,
        cashDeposit: 0,
        totalDeposit: 0,
        financeAmount: initialFormData.carDetails.price,
      },
      consent: {
        dataConsent: false,
      },
      carDetails: initialFormData.carDetails,
      hiddenFields: initialFormData.hiddenFields,
    },
  });

  const {
    handleSubmit,
    setError,
    setFocus,
    trigger,
    formState: { isDirty },
  } = methods;

  const { mutate, isPending } = useSubmitFinanceForm({
    onValidationError: (validationErrors) => {
      for (const error of validationErrors) {
        setError(error.field as Path<CloseBrothersForm>, {
          message: error.message,
        });
      }
      setFocus(validationErrors[0].field as Path<CloseBrothersForm>);
      const fieldsWithErrors = validationErrors.map((error) => error.field);
      onSubmitErrorCallback(fieldsWithErrors);
    },
    onSuccess: onSubmitCallback,
  });

  const buildPayload = (values: CloseBrothersForm) => ({
    ...values,
    personalDetails: {
      ...values.personalDetails,
      dateOfBirth: formatDateOfBirth(values.personalDetails.dateOfBirth),
    },
    /**
     * We exclude the previousAddress fields entirely since Close Brothers expects an empty object
     * rather than fields with empty values if no previous address is applicable
     */
    previousAddress: {
      ...(Number(values.currentAddress.timeAtCurrentAddress.years) <=
      THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_CLOSE_BROTHERS
        ? values.previousAddress
        : {}),
    },
  });

  const onSubmit: OnSubmitHandler<typeof methods> = async (values) => {
    mutate(buildPayload(values));
  };

  const onInvalidCloseBrothersSubmission: SubmitErrorHandler<
    CloseBrothersForm
  > = async (errors) => {
    const fieldsWithErrors = typeSafeObjectKeys(errors);
    onSubmitErrorCallback(fieldsWithErrors);
  };

  const { mutate: validateStep, isPending: isValidating } = useValidateStep({
    onValidationError: (validationErrors) => {
      for (const error of validationErrors) {
        setError(error.field as Path<CloseBrothersForm>, {
          message: error.message,
        });
      }
      scrollToFirstError();
    },
    onSuccess: goToNextStep,
  });

  const handleAdvanceStep = async (fields: Path<CloseBrothersForm>[]) => {
    const isValid = await trigger(fields);
    if (!isValid) {
      scrollToFirstError();
      return;
    }
    validateStep({
      step: currentStep,
      payload: buildPayload(methods.getValues()),
    });
  };

  const handleSubmitStep = handleSubmit(
    onSubmit,
    onInvalidCloseBrothersSubmission,
  );

  const steps: StepConfig[] = [
    {
      title: 'Personal details',
      content: (
        <PersonalDetails
          displayCountryOfBirth
          displayMaidenName
          displayNumberOfDependants
          displayCurrentVehicleRegistrationNumber
          minApplicantAge={CLOSE_BROTHERS_MIN_AGE}
          maxApplicantAge={CLOSE_BROTHERS_MAX_AGE}
        />
      ),
      onNext: async () => {
        const isBaseValid = await trigger(STEP_2_FIELDS);

        // zodResolver does not surface superRefine errors when trigger() is
        // called with specific field paths — same workaround as step 3.
        const { title, maidenName } = methods.getValues('personalDetails');
        const maidenNameMissing = FEMALE_TITLES.includes(title) && !maidenName;

        if (maidenNameMissing) {
          setError('personalDetails.maidenName', {
            type: 'custom',
            message: 'Maiden Name cannot be empty',
          });
        }

        if (!isBaseValid || maidenNameMissing) {
          scrollToFirstError();
          return;
        }

        validateStep({
          step: currentStep,
          payload: buildPayload(methods.getValues()),
        });
      },
      isSubmitting: isValidating,
    },
    {
      title: 'Residential details',
      content: <ResidentialDetails />,
      onNext: async () => {
        const years = methods.getValues(
          'currentAddress.timeAtCurrentAddress.years',
        );
        const showPreviousAddress =
          years !== '' &&
          Number(years) <=
            THRESHOLD_TO_SHOW_PREVIOUS_ADDRESS_FIELDS_IN_YEARS_CLOSE_BROTHERS;

        const isBaseValid = await trigger(STEP_3_FIELDS);

        if (!showPreviousAddress) {
          if (isBaseValid)
            validateStep({
              step: currentStep,
              payload: buildPayload(methods.getValues()),
            });
          else scrollToFirstError();
          return;
        }

        // zodResolver does not surface superRefine errors for conditional nested
        // objects when trigger() is called with specific field paths — the
        // cross-field refinement runs at schema root level but its issues are
        // never mapped back to the triggered fields. We validate manually here
        // so the user sees inline errors on empty required previous address fields.
        const prevValues = methods.getValues('previousAddress');
        const hasPrevAddressErrors = setRequiredFieldErrors<CloseBrothersForm>(
          setError,
          [
            ['previousAddress.addressLine1', !prevValues?.addressLine1],
            ['previousAddress.town', !prevValues?.town],
            ['previousAddress.county', !prevValues?.county],
            [
              'previousAddress.timeAtAddress.years',
              !prevValues?.timeAtAddress?.years,
            ],
          ],
        );

        if (isBaseValid && !hasPrevAddressErrors) {
          validateStep({
            step: currentStep,
            payload: buildPayload(methods.getValues()),
          });
        } else {
          scrollToFirstError();
        }
      },
      isSubmitting: isValidating,
    },
    {
      title: 'Employment details',
      content: <EmploymentDetails />,
      onNext: () => handleAdvanceStep(STEP_4_FIELDS),
      isSubmitting: isValidating,
    },
    {
      title: 'Banking information',
      content: <BankingInformation />,
      onNext: () => handleAdvanceStep(STEP_5_FIELDS),
      isSubmitting: isValidating,
    },
    {
      title: 'Data protection and consent',
      content: <DataProtectionAndConsents dealerName={query.dealerName} />,
      onNext: handleSubmitStep,
      nextLabel: 'Submit Application',
      isSubmitting: isPending,
    },
  ];

  const step1Content = (
    <Step1Content
      query={query}
      introConsent={introConsent}
      setIntroConsent={setIntroConsent}
      currentStep={currentStep}
      totalSteps={TOTAL_STEPS}
      adId={adId}
      showOutstandingFinance
      maxFinanceAmount={CLOSE_BROTHERS_MAX_FINANCE_AMOUNT}
      isValidating={isValidating}
      onContinue={async () => {
        const isValid = await trigger(STEP_1_FIELDS);
        if (!isValid) {
          scrollToFirstError();
          return;
        }
        validateStep({ step: 1, payload: buildPayload(methods.getValues()) });
      }}
    />
  );

  return (
    <ProviderFormShell
      methods={methods}
      minimumDepositValue={query.minimumDepositValue}
      isDirty={isDirty}
      currentStep={currentStep}
      totalSteps={TOTAL_STEPS}
      steps={steps}
      goToPrevStep={goToPrevStep}
      step1Content={step1Content}
    />
  );
};

export { CloseBrothersFinanceForm };
